import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {
  ActionIcon,
  ThemeIcon,
  Badge,
  Button,
  ButtonProps,
  Flex,
  type MantineColor,
  Title,
  Tooltip,
  createStyles,
} from '@mantine/core';
import React, {type RefObject, useEffect, useRef, useState} from 'react';

import {getDisabledTooltipButtonProps} from 'lib/a11y/ButtonA11y';
import Breadcrumb, {type BreadcrumbItem} from 'ui-library/Breadcrumb';
import DropdownButton from 'ui-library/buttons/DropdownButton';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import EditableTextField, {TextFieldType} from 'ui-library/inputs/EditableTextField';
import {type MantineDropdownMenuSection} from 'ui-library/menus/MantineDropdownMenu';
import MantineOverflowMenu from 'ui-library/menus/MantineOverflowMenu';

export interface BadgeInfo {
  text: string;
  color: MantineColor;
  icon?: BenchlingIconName;
}

interface FullTextButtonBase {
  label: string;
  variant: string;
  onClick: () => void;
  disabled?: boolean;
  /** When false, this button stays enabled even when the Header is locked. Defaults to true. */
  isLockable?: boolean;
  icon?: BenchlingIconName;
  classNames?: ButtonProps['classNames'];
  dataTestAttrs?: DataTestAttrs;
}

type FullTextButtonWithOverflow = FullTextButtonBase & {
  overflow: Array<MantineDropdownMenuSection>;
  disabledTooltip?: never;
};

type FullTextButtonWithoutOverflow = FullTextButtonBase & {
  /** Tooltip explaining why the button is disabled. Only shown while the button is disabled. */
  disabledTooltip?: string;
  overflow?: never;
};

/**
 * A full-text header button. `disabledTooltip` and `overflow` are mutually exclusive, since an
 * overflow button renders a DropdownButton which doesn't support tooltips.
 */
export type FullTextButton = FullTextButtonWithOverflow | FullTextButtonWithoutOverflow;

export interface ActionItem {
  icon: BenchlingIconName;
  /** Shown as tooltip on the icon button, and as the label in the overflow menu. */
  name: string;
  onSelect?: () => void;
  isDisabled?: boolean;
  /** Shown as a tooltip on the item when it is disabled and in the overflow menu. */
  disabledTooltip?: string;
  /** When false, this button stays enabled even when the Header is locked. Defaults to true. */
  isLockable?: boolean;
  /** Applies to the inner BenchlingIcon */
  className?: string;
  dataTestAttrs?: DataTestAttrs;
}

export interface HeaderClassNames {
  /** Classnames to be applied to the outermost wrapper of the header */
  body?: string;
}

export interface HeaderProps {
  /** Path crumbs leading to this item, not including the title itself. */
  crumbs?: Array<BreadcrumbItem>;
  /** Icon shown to the left of the breadcrumb row. */
  icon?: BenchlingIconName;
  /** Color of the ThemeIcon wrapping the header icon. Defaults to 'gray'. */
  iconColor?: MantineColor;

  title: string;
  /** When provided, title becomes an editable field. */
  onTitleChange?: (newTitle: string) => void;
  titleMaxLength?: number;

  badges?: Array<BadgeInfo>;

  /** Right-aligned content. First 3 render as icon buttons; extras collapse into an overflow menu. */
  fullTextButtons?: Array<FullTextButton>;
  actionIconButtons?: Array<ActionItem>;
  miscItems?: Array<JSX.Element>;

  /** When true, collapses the title row down to a compact breadcrumb. */
  isCollapsed?: boolean;
  /** When true, adds a Locked badge and disables all inputs and action buttons. */
  isLocked?: boolean;

  /**
   * Optional classnames to be applied to components that are always present. In order to style individual
   * icons, buttons, etc.; you must pass those classnames as part of the specs in `actionIconButtons`,
   * `fullTextButtons`, etc.
   */
  classNames?: HeaderClassNames;

  dataTestAttrs?: DataTestAttrs;

  /** Shown below the title row, hidden when collapsed. */
  children?: React.ReactNode;
}

/** Max visible icon buttons before the overflow menu appears; when overflow is needed, N-1 buttons show to make room for the overflow trigger. */
const MAX_INLINE_ACTION_BUTTONS = 3;

interface HeaderBadgesProps {
  badges?: Array<BadgeInfo>;
  isLocked: boolean;
  isCollapsed: boolean;
}

function HeaderBadges({badges, isLocked, isCollapsed}: HeaderBadgesProps): JSX.Element {
  const size = isCollapsed ? 'sm' : 'md';
  return (
    <Flex gap="sm" {...getTestProps({component: 'Header', element: 'badges'})}>
      {badges?.map(({text, color, icon: badgeIcon}) => (
        <Badge
          key={text}
          color={color}
          size={size}
          leftSection={badgeIcon && <BenchlingIcon icon={badgeIcon} />}
        >
          {text}
        </Badge>
      ))}
      {isLocked && (
        <Badge color="yellow" size={size} leftSection={<BenchlingIcon icon="lock" />}>
          Locked
        </Badge>
      )}
    </Flex>
  );
}

interface HeaderFullTextButtonsProps {
  buttons: Array<FullTextButton>;
  isLocked: boolean;
  isCollapsed: boolean;
}

function HeaderFullTextButtons({buttons, isLocked, isCollapsed}: HeaderFullTextButtonsProps): JSX.Element {
  const {classes} = useStyles();
  const size = isCollapsed ? 'xs' : 'sm';
  return (
    <div
      className={classes.inlineContainer}
      {...getTestProps({component: 'Header', element: 'fullTextButtons'})}
    >
      {buttons.map(
        ({
          label,
          variant,
          onClick,
          disabled,
          disabledTooltip,
          isLockable = true,
          icon: buttonIcon,
          classNames,
          overflow,
          dataTestAttrs: buttonDataTestAttrs,
        }) => {
          const leftIcon = buttonIcon ? <BenchlingIcon icon={buttonIcon} /> : undefined;
          const isDisabled = disabled || (isLockable && isLocked);

          if (overflow && overflow.length > 0) {
            return (
              <DropdownButton
                key={label}
                variant={variant}
                size={size}
                disabled={isDisabled}
                leftIcon={leftIcon}
                onClick={onClick}
                sections={overflow}
                classNames={classNames}
                dataTestAttrs={buttonDataTestAttrs}
              >
                {label}
              </DropdownButton>
            );
          }

          return (
            <Tooltip
              key={label}
              label={disabledTooltip}
              position="top"
              disabled={!(isDisabled && disabledTooltip != null)}
            >
              <Button
                variant={variant}
                size={size}
                leftIcon={leftIcon}
                classNames={classNames}
                {...getDisabledTooltipButtonProps(isDisabled, onClick)}
                {...(buttonDataTestAttrs ? getTestProps(buttonDataTestAttrs) : {})}
              >
                {label}
              </Button>
            </Tooltip>
          );
        }
      )}
    </div>
  );
}

interface HeaderActionIconButtonsProps {
  items: Array<ActionItem>;
  isLocked: boolean;
  isCollapsed: boolean;
}

function HeaderActionIconButtons({items, isLocked, isCollapsed}: HeaderActionIconButtonsProps): JSX.Element {
  const {classes} = useStyles();
  const needsOverflow = items.length > MAX_INLINE_ACTION_BUTTONS;
  const visibleItems = items.slice(
    0,
    needsOverflow ? MAX_INLINE_ACTION_BUTTONS - 1 : MAX_INLINE_ACTION_BUTTONS
  );
  const overflowItems = needsOverflow
    ? items
        .slice(MAX_INLINE_ACTION_BUTTONS - 1)
        .map(
          ({name, icon: itemIcon, onSelect, isDisabled, disabledTooltip, className, isLockable = true}) => ({
            name,
            icon: itemIcon,
            onSelect,
            isDisabled: isDisabled || (isLockable && isLocked),
            disabledTooltip,
            className,
          })
        )
    : [];

  return (
    <div
      className={classes.inlineContainer}
      {...getTestProps({component: 'Header', element: 'actionIconButtons'})}
    >
      {visibleItems.map(
        ({
          name,
          onSelect,
          isDisabled,
          disabledTooltip,
          isLockable = true,
          className,
          dataTestAttrs: itemDataTestAttrs,
          icon: itemIcon,
        }) => {
          const isItemDisabled = (isDisabled ?? false) || (isLockable && isLocked);
          return (
            <Tooltip
              key={name}
              label={isItemDisabled && disabledTooltip != null ? disabledTooltip : name}
              position="top"
            >
              <ActionIcon
                aria-label={name}
                size={isCollapsed ? 'sm' : 'md'}
                {...getDisabledTooltipButtonProps(isItemDisabled, onSelect ?? noop)}
                {...(itemDataTestAttrs ? getTestProps(itemDataTestAttrs) : {})}
              >
                <BenchlingIcon className={className} icon={itemIcon} />
              </ActionIcon>
            </Tooltip>
          );
        }
      )}
      {overflowItems.length > 0 && (
        <MantineOverflowMenu
          sections={[{items: overflowItems}]}
          iconSize={isCollapsed ? 'sm' : 'md'}
          tooltipPosition="top"
        />
      )}
    </div>
  );
}

export default function Header({
  crumbs = [],
  icon,
  iconColor = 'gray',
  title,
  onTitleChange,
  titleMaxLength,
  badges,
  fullTextButtons,
  actionIconButtons = [],
  miscItems,
  isCollapsed = false,
  isLocked = false,
  dataTestAttrs,
  classNames,
  children,
}: HeaderProps): JSX.Element {
  const {classes, cx} = useStyles();
  const collapsedItems: Array<BreadcrumbItem> = [...crumbs, {label: title}];
  const expandedItems: Array<BreadcrumbItem> = crumbs;

  return (
    <Flex
      direction="column"
      gap="sm"
      className={cx(classes.body, classNames?.body)}
      aria-expanded={!isCollapsed}
      {...getTestProps({component: 'Header', element: 'root'})}
      {...(dataTestAttrs ? getTestProps(dataTestAttrs) : {})}
    >
      <Flex align="center" justify="space-between">
        <Flex direction="column" align="flex-start" gap="xs">
          <Flex align="center" gap="md">
            {isCollapsed && icon && (
              <ThemeIcon variant="light" size="md" color={iconColor}>
                <BenchlingIcon icon={icon} />
              </ThemeIcon>
            )}
            <Breadcrumb
              items={isCollapsed ? collapsedItems : expandedItems}
              isLastItemInteractable={!isCollapsed && expandedItems.length > 0}
              boldLastItem={isCollapsed}
            />
            {isCollapsed && (badges?.length || isLocked) && (
              <HeaderBadges badges={badges} isLocked={isLocked} isCollapsed={isCollapsed} />
            )}
          </Flex>
          {!isCollapsed && (
            <Flex justify="center" align="center" gap="sm">
              {icon && (
                <ThemeIcon variant="light" size="lg" color={iconColor}>
                  <BenchlingIcon icon={icon} />
                </ThemeIcon>
              )}
              {onTitleChange ? (
                <EditableTextField
                  value={title}
                  maxLength={titleMaxLength}
                  requestChange={onTitleChange}
                  type={TextFieldType.TEXT}
                  isDisabled={isLocked}
                  emptyValueAllowed={false}
                  initialIsBeingEdited={false}
                  onIsBeingEditedChanged={() => {}}
                  {...getTestProps({component: 'Header', element: 'title'})}
                />
              ) : (
                <Title order={5} {...getTestProps({component: 'Header', element: 'title'})}>
                  {title}
                </Title>
              )}
              <HeaderBadges badges={badges} isLocked={isLocked} isCollapsed={isCollapsed} />
            </Flex>
          )}
        </Flex>
        <Flex direction="row-reverse" align="center" gap="xs">
          {fullTextButtons && (
            <HeaderFullTextButtons buttons={fullTextButtons} isLocked={isLocked} isCollapsed={isCollapsed} />
          )}
          <HeaderActionIconButtons items={actionIconButtons} isLocked={isLocked} isCollapsed={isCollapsed} />
          {miscItems && (
            <div
              className={classes.inlineContainer}
              {...getTestProps({component: 'Header', element: 'miscItems'})}
            >
              {miscItems}
            </div>
          )}
        </Flex>
      </Flex>

      {!isCollapsed && children && (
        <Flex {...getTestProps({component: 'Header', element: 'children'})}>{children}</Flex>
      )}
    </Flex>
  );
}

const COLLAPSE_DEADZONE_PX = 10;

/**
 * Tracks scroll position of a container and returns whether the Header should be collapsed.
 * Pass the returned ref to the scrollable container div, and isCollapsed to the Header.
 *
 * @param threshold - Scroll offset in px before collapsing. Defaults to 150.
 */

export function useHeaderCollapse(threshold = 150): {
  scrollRef: RefObject<HTMLDivElement>;
  isCollapsed: boolean;
} {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    function handleScroll(): void {
      const scrollTop = el?.scrollTop ?? 0;
      setIsCollapsed((prev) => {
        if (!prev && scrollTop > threshold) {
          return true;
        }
        if (prev && scrollTop < threshold - COLLAPSE_DEADZONE_PX) {
          return false;
        }
        return prev;
      });
    }

    el.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, [threshold]);

  return {scrollRef, isCollapsed};
}

const useStyles = createStyles((theme) => ({
  body: {
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    backgroundColor: theme.white,
    // Custom shadow; theme.shadows.sm is a multi-layer elevation shadow that doesn't match this directional drop shadow style
    boxShadow: `0 2px 4px 0 ${theme.fn.rgba(theme.colors.gray[5], 0.25)}`,
    alignSelf: 'stretch',
  },
  inlineContainer: {
    display: 'contents',
  },
}));
